import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FileText, Plus, Download, Send, CheckCircle, XCircle, Loader2, AlertTriangle, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const BACKEND_BASE = process.env.REACT_APP_BACKEND_URL || '';
const authHeaders = () => {
  const t = localStorage.getItem('crm_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const DOC_TYPE_LABEL = { invoice: 'Rechnung', offer: 'Angebot' };
const STATUS_META = {
  draft:     { label: 'Entwurf',     cls: 'bg-gray-100 text-gray-700' },
  sent:      { label: 'Versendet',   cls: 'bg-blue-100 text-blue-700' },
  paid:      { label: 'Bezahlt',     cls: 'bg-emerald-100 text-emerald-700' },
  overdue:   { label: 'Überfällig',  cls: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Storniert',   cls: 'bg-amber-100 text-amber-700' },
};

function fmtEur(cents) {
  const eur = (cents || 0) / 100;
  return eur.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
}
function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('de-DE'); } catch { return '—'; }
}

export default function LeadDocumentsSection({ lead, customerId }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = { lead_id: lead.id };
      const { data } = await axios.get(`${API}/admin/documents`, { headers: authHeaders(), params });
      setDocuments(data || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Laden fehlgeschlagen');
    }
    setLoading(false);
  }, [lead.id]);

  useEffect(() => { load(); }, [load]);

  const action = async (docId, kind) => {
    setBusyId(docId); setError('');
    try {
      if (kind === 'regenerate') {
        await axios.post(`${API}/admin/documents/${docId}/regenerate-pdf`, {}, { headers: authHeaders() });
      } else if (kind === 'send') {
        if (!window.confirm('Dokument per Mail versenden? Empfänger ist die hinterlegte Rechnungs-Adresse.')) { setBusyId(null); return; }
        await axios.post(`${API}/admin/documents/${docId}/send`, {}, { headers: authHeaders() });
      } else if (kind === 'paid') {
        if (!window.confirm('Als bezahlt markieren?')) { setBusyId(null); return; }
        await axios.post(`${API}/admin/documents/${docId}/mark-paid`, {}, { headers: authHeaders() });
      } else if (kind === 'cancel') {
        const reason = window.prompt('Stornogrund? (wird in den Notes vermerkt)');
        if (reason === null) { setBusyId(null); return; }
        await axios.post(`${API}/admin/documents/${docId}/cancel`, { reason: reason || '' }, { headers: authHeaders() });
      }
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Aktion fehlgeschlagen');
    }
    setBusyId(null);
  };

  return (
    <div className="border-t border-gray-100 pt-4 mt-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-xs uppercase tracking-wider text-gray-500 font-bold inline-flex items-center gap-1.5" style={{ fontFamily: "'Oswald', sans-serif" }}>
          <FileText size={13} /> Dokumente ({documents.length})
        </h4>
        <div className="flex items-center gap-1.5">
          <button onClick={load} className="text-[10px] uppercase tracking-wider text-gray-500 hover:text-[#1f1f1f] inline-flex items-center gap-1 px-2 py-1">
            <RefreshCw size={11} />
          </button>
          {customerId && (
            <button onClick={() => setShowCreate(true)}
              className="bg-[#0a0a0a] text-[#befa0a] text-[10px] uppercase tracking-wider font-bold px-2.5 py-1 hover:bg-[#befa0a] hover:text-[#0a0a0a] inline-flex items-center gap-1"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              <Plus size={11} /> Neu
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 p-2 text-xs mb-2">
          <AlertTriangle size={12} className="inline mr-1" /> {error}
        </div>
      )}

      {loading ? (
        <p className="text-xs text-gray-400 italic"><Loader2 size={12} className="inline animate-spin mr-1" /> Lade…</p>
      ) : documents.length === 0 ? (
        <p className="text-xs text-gray-400 italic">
          Noch keine Dokumente. {customerId ? 'Klick "+ Neu" für Angebot/Rechnung.' : 'Erst Modul-1-Start (legt Customer an), dann sind Dokumente möglich.'}
        </p>
      ) : (
        <ul className="space-y-2">
          {documents.map((d) => {
            const meta = STATUS_META[d.status] || STATUS_META.draft;
            const busy = busyId === d.id;
            const isInvoice = d.doc_type === 'invoice';
            return (
              <li key={d.id} className="border border-gray-200 bg-white p-3 text-xs">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <div className="min-w-0">
                    <p className="font-bold text-[#1f1f1f]" style={{ fontFamily: "'Oswald', sans-serif" }}>
                      {DOC_TYPE_LABEL[d.doc_type] || d.doc_type} {d.doc_number}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5">
                      {fmtDate(d.created_at)} · {d.product_key || '—'} · {fmtEur(d.total_cents)}
                    </p>
                  </div>
                  <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${meta.cls}`}>
                    {meta.label}
                  </span>
                </div>
                {d.sent_failure && (
                  <p className="text-[10px] text-red-600 mt-1">⚠ Versand-Fehler: {d.sent_failure.slice(0, 120)}</p>
                )}
                <div className="flex flex-wrap items-center gap-1.5 mt-2">
                  {d.pdf_path && (
                    <a href={`${BACKEND_BASE}/api/uploads/${d.pdf_path}`} target="_blank" rel="noopener noreferrer"
                      className="text-[10px] uppercase tracking-wider border border-gray-300 text-gray-700 px-2 py-1 hover:bg-gray-50 inline-flex items-center gap-1"
                      style={{ fontFamily: "'Oswald', sans-serif" }}>
                      <Download size={10} /> PDF
                    </a>
                  )}
                  <button onClick={() => action(d.id, 'regenerate')} disabled={busy}
                    className="text-[10px] uppercase tracking-wider border border-gray-300 text-gray-700 px-2 py-1 hover:bg-gray-50 disabled:opacity-50"
                    style={{ fontFamily: "'Oswald', sans-serif" }}>
                    PDF neu rendern
                  </button>
                  {d.status !== 'cancelled' && (
                    <button onClick={() => action(d.id, 'send')} disabled={busy}
                      className="text-[10px] uppercase tracking-wider bg-[#0a0a0a] text-[#befa0a] px-2 py-1 hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50 inline-flex items-center gap-1"
                      style={{ fontFamily: "'Oswald', sans-serif" }}>
                      <Send size={10} /> {d.sent_at ? 'Neu senden' : 'Senden'}
                    </button>
                  )}
                  {isInvoice && d.status === 'sent' && (
                    <button onClick={() => action(d.id, 'paid')} disabled={busy}
                      className="text-[10px] uppercase tracking-wider bg-[#658a07] text-white px-2 py-1 hover:bg-[#4f6c05] disabled:opacity-50 inline-flex items-center gap-1"
                      style={{ fontFamily: "'Oswald', sans-serif" }}>
                      <CheckCircle size={10} /> Bezahlt
                    </button>
                  )}
                  {d.status !== 'cancelled' && d.status !== 'paid' && (
                    <button onClick={() => action(d.id, 'cancel')} disabled={busy}
                      className="text-[10px] uppercase tracking-wider border border-red-300 text-red-600 px-2 py-1 hover:bg-red-50 disabled:opacity-50 inline-flex items-center gap-1"
                      style={{ fontFamily: "'Oswald', sans-serif" }}>
                      <XCircle size={10} /> Stornieren
                    </button>
                  )}
                  {busy && <Loader2 size={11} className="animate-spin text-gray-400" />}
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {showCreate && customerId && (
        <CreateDocumentModal
          customerId={customerId}
          leadId={lead.id}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); load(); }}
        />
      )}
    </div>
  );
}


function CreateDocumentModal({ customerId, leadId, onClose, onCreated }) {
  const [products, setProducts] = useState({});
  const [docType, setDocType] = useState('invoice');
  const [productKey, setProductKey] = useState('modul_1');
  const [quantity, setQuantity] = useState(1);
  const [sendNow, setSendNow] = useState(true);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API}/admin/documents/_meta/products`, { headers: authHeaders() })
      .then(({ data }) => setProducts(data || {}))
      .catch(() => setProducts({}));
  }, []);

  const product = products[productKey] || {};
  const isManntag = product.unit === 'Manntag';

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError('');
    try {
      await axios.post(`${API}/admin/documents`, {
        doc_type: docType,
        customer_id: customerId,
        product_key: productKey,
        quantity: isManntag ? parseInt(quantity, 10) : 1,
        lead_id: leadId,
        notes,
        send_now: sendNow,
      }, { headers: authHeaders() });
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erstellen fehlgeschlagen');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-[480px]">
        <div className="bg-[#0a0a0a] text-white px-5 py-3 flex items-center justify-between">
          <h3 className="text-base font-bold uppercase" style={{ fontFamily: "'Oswald', sans-serif" }}>
            Neues Dokument
          </h3>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 p-2 text-xs">{error}</div>}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Typ</label>
              <select value={docType} onChange={(e) => setDocType(e.target.value)}
                className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]">
                <option value="invoice">Rechnung</option>
                <option value="offer">Angebot</option>
              </select>
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Produkt</label>
              <select value={productKey} onChange={(e) => setProductKey(e.target.value)}
                className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]">
                {Object.entries(products).map(([key, p]) => (
                  <option key={key} value={key}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>
          {isManntag && (
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Manntage</label>
              <input type="number" min={1} max={999} value={quantity} onChange={(e) => setQuantity(e.target.value)}
                className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
              <p className="text-[10px] text-gray-500 mt-1">
                {product.price_net_eur} € pro Manntag · Gesamt: {(parseInt(quantity, 10) || 0) * (product.price_net_eur || 0)} € netto
              </p>
            </div>
          )}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-gray-500 mb-1 font-bold">Notiz (intern)</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
              className="w-full border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:border-[#befa0a]" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={sendNow} onChange={(e) => setSendNow(e.target.checked)} />
            <span className="text-xs text-[#1f1f1f]">PDF generieren + sofort per Mail an Kunde senden</span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="text-xs uppercase tracking-wider text-gray-500 hover:text-[#1f1f1f] px-3 py-2"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              Abbrechen
            </button>
            <button type="submit" disabled={saving}
              className="bg-[#0a0a0a] text-[#befa0a] px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#befa0a] hover:text-[#0a0a0a] disabled:opacity-50 inline-flex items-center gap-1"
              style={{ fontFamily: "'Oswald', sans-serif" }}>
              {saving ? <Loader2 size={12} className="animate-spin" /> : null}
              {sendNow ? 'Erstellen + senden' : 'Erstellen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
